import { useEffect, useRef, useState } from "react";

interface UseDelayedHoverOptions {
  delayMs?: number;
  onHoverStart?: () => void;
}

/**
 * A hook that delays the hover state of a component.
 * @param delayMs - The delay in milliseconds before the hover state is set.
 * @param onHoverStart - A function to call when the hover state is set.
 * @returns An object with the isHovered state and handlers for the mouse enter and leave events.
 */
export function useDelayedHover({
  delayMs = 200,
  onHoverStart,
}: UseDelayedHoverOptions = {}) {
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current !== null) {
        window.clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleEnter = () => {
    if (hoverTimeoutRef.current !== null) {
      window.clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = window.setTimeout(() => {
      setIsHovered(true);
      onHoverStart?.();
    }, delayMs);
  };

  const handleLeave = () => {
    if (hoverTimeoutRef.current !== null) {
      window.clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(false);
  };

  return {
    isHovered,
    handlers: {
      onMouseEnter: handleEnter,
      onMouseLeave: handleLeave,
    },
  };
}
